import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { Container, Reveal } from "@/components/Reveal";
import ServiceIcon from "@/components/ServiceIcon";
import { ArticleCard } from "@/components/education/ArticleCard";
import { getCategory, articlesByCategory, CATEGORIES } from "@/data/education";

export default function CategoryPage() {
  const { categorySlug } = useParams();
  const category = getCategory(categorySlug);
  const [q, setQ] = useState("");

  const all = useMemo(
    () => (category ? articlesByCategory(category.slug) : []),
    [category]
  );
  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return all;
    return all.filter(
      (a) => a.title.toLowerCase().includes(query) || a.excerpt.toLowerCase().includes(query)
    );
  }, [all, q]);

  if (!category) return <Navigate to="/education" replace />;
  if (category.external) return <Navigate to={category.external} replace />;

  const recent = [...all].sort((a, b) => new Date(b.added) - new Date(a.added));
  const featured = all.filter((a) => a.featured);
  const related = CATEGORIES.filter((c) => c.slug !== category.slug).slice(0, 4);

  return (
    <>
      <SEO
        title={category.label}
        description={category.description}
        path={`/education/category/${category.slug}`}
      />
      <PageHero
        overline="Education Center · Category"
        titleLines={[category.label]}
        subtitle={category.description}
        crumbs={[
          { label: "Education Center", path: "/education" },
          { label: category.label, path: `/education/category/${category.slug}` },
        ]}
        image={category.image}
      />

      <section className="bg-white py-20 md:py-24">
        <Container>
          {/* Search within category */}
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-navy md:text-3xl">
              {all.length} article{all.length !== 1 ? "s" : ""} in {category.label}
            </h2>
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-slatey" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search within category…"
                data-testid="category-search"
                className="w-full border-0 border-b border-line bg-transparent py-3 pl-6 pr-0 text-navy outline-none transition-colors placeholder:text-slatey/60 focus:border-electric"
              />
            </div>
          </div>

          {featured.length > 0 && q === "" && (
            <div className="mt-12">
              <p className="overline text-electric">Featured</p>
              <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                {featured.slice(0, 3).map((a) => (
                  <Reveal key={a.slug}>
                    <ArticleCard article={a} />
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          <div className="mt-12">
            <p className="overline text-electric">{q ? "Results" : "All Articles"}</p>
            {filtered.length > 0 ? (
              <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {(q ? filtered : recent).map((a, i) => (
                  <Reveal key={a.slug} delay={(i % 3) * 0.05}>
                    <ArticleCard article={a} />
                  </Reveal>
                ))}
              </div>
            ) : (
              <p className="mt-6 text-slatey">
                No articles match “{q}”.{" "}
                <Link to="/education/search" className="font-medium text-electric hover:underline">
                  Search the full library
                </Link>
                .
              </p>
            )}
          </div>

          {/* Related categories */}
          <div className="mt-20 border-t border-line pt-12">
            <p className="overline text-electric">Related Categories</p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((c) => (
                <Link
                  key={c.slug}
                  to={c.external || `/education/category/${c.slug}`}
                  className="group flex items-center gap-4 border border-line p-5 transition-colors hover:border-electric"
                >
                  <span
                    className="flex h-10 w-10 items-center justify-center rounded-sm"
                    style={{ backgroundColor: `${c.color}14`, color: c.color }}
                  >
                    <ServiceIcon name={c.icon} className="h-5 w-5" />
                  </span>
                  <span className="flex-1 text-sm font-medium text-navy">{c.label}</span>
                  <ArrowUpRight className="h-4 w-4 text-slatey transition-colors group-hover:text-electric" />
                </Link>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <CTABand />
    </>
  );
}
